/**
 * 导入 BRep 运行时补齐的聚合失败。
 *
 * 生命周期边界：该错误只在一个导入拓扑源的全部 body 校验或注册结束后创建；宿主收到它时
 * 必须放弃当前 staged Worker 与场景事务，不能把资产降级成可编辑或可保存的部分成功状态。
 */
export class ImportedBrepHydrationError extends AggregateError {
  /** 跨包或重复 bundle 仍可稳定识别的错误分类码。 */
  readonly code = 'IMPORTED_BREP_HYDRATION_FAILED'

  /**
   * 创建包含全部 body 诊断的严格 hydration 错误。
   *
   * @param failures 当前导入拓扑源内收集到的 body 级失败。
   */
  constructor(failures: readonly Error[]) {
    const copiedFailures = [...failures]
    super(
      copiedFailures,
      `导入 BRep hydration 失败（${copiedFailures.length} 项）：${copiedFailures
        .map((failure) => failure.message)
        .join(' | ')}`,
    )
    this.name = 'ImportedBrepHydrationError'
  }
}

/**
 * 判断未知异常是否属于导入 BRep hydration 完整性失败。
 *
 * 设计说明：web-client 与 cad-core 可能由不同 bundle 实例加载，单独依赖 `instanceof` 会在
 * 跨 realm 或重复依赖时失效，因此同时使用稳定 code；名称只作为旧实例的迁移识别信号。
 *
 * @param error 待分类异常。
 * @returns 属于 hydration 完整性失败时返回 `true`。
 */
export function isImportedBrepHydrationError(error: unknown): error is ImportedBrepHydrationError {
  if (!error || typeof error !== 'object') {
    return false
  }

  return error instanceof ImportedBrepHydrationError
    || Reflect.get(error, 'code') === 'IMPORTED_BREP_HYDRATION_FAILED'
    || (
      Reflect.get(error, 'name') === 'ImportedBrepHydrationError'
      && Array.isArray(Reflect.get(error, 'errors'))
    )
}

/**
 * 将 hydration 阶段抛出的任意异常包装成稳定的完整性错误。
 *
 * @param error hydration 阶段捕获的异常。
 * @returns 已有分类错误保持对象身份，其余异常转换为单项聚合错误。
 */
export function toImportedBrepHydrationError(error: unknown): ImportedBrepHydrationError {
  if (isImportedBrepHydrationError(error)) {
    return error
  }

  const failure = error instanceof Error ? error : new Error(String(error))
  return new ImportedBrepHydrationError([failure])
}
